import ErrorManager from '@/common/utils/ErrorManager';
import {
    PreventiveChartWidgetItems,
    PreventiveCircleWidgetItems,
    PreventiveDateParameters
} from '@/models/PreventiveWidgetItems';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import HomeService from '@/modules/Home/services/HomeService';
import { RootState } from '@/plugins/store';
import { ActionTree, Commit, MutationTree } from 'vuex';
import { PREVENTIVE_WIDGET } from '../mutation-types';

export interface PreventiveState {
    preventiveCircleItems: PreventiveCircleWidgetItems;
    preventiveChartItems: PreventiveChartWidgetItems;
    isCircleLoading: boolean;
    isChartLoading: boolean;
    error: boolean;
}

interface PreventiveData {
    debutPeriode: string;
    finPeriode: string;
    nbARealiser: number;
    nbEnCours: number;
    nbRealisees: number;
}

export enum PreventiveAction {
    GET_PREVENTIVE_CIRCLE_DATA = 'GET_PREVENTIVE_CIRCLE_DATA',
    GET_PREVENTIVE_CHART_DATA = 'GET_PREVENTIVE_CHART_DATA',
}
interface PreventiveActionType {
    commit: Commit;
    state: PreventiveState;
}

export const initialState = (): PreventiveState => ({
    preventiveCircleItems: {
        inProgressTotal: 0,
        achievedTotal: 0,
        awaitingRealizationTotal: 0,
    },
    preventiveChartItems: {
        inProgress: [],
        achieved: [],
        awaitingRealization: [],
    },
    isCircleLoading: false,
    isChartLoading: false,
    error: false,
});

const actions: ActionTree<PreventiveState, RootState> = {
    async [PreventiveAction.GET_PREVENTIVE_CIRCLE_DATA](
        { commit, state }: PreventiveActionType,
        payload: PreventiveDateParameters
    ): Promise<void> {
        commit(PREVENTIVE_WIDGET.SET_CIRCLE_LOADING, true);
        await HomeService.getPreventiveCircleData(payload)
            .then(async response => {
                if (response !== undefined || response.status !== undefined) {
                    if (response.status !== 200) {
                        commit(PREVENTIVE_WIDGET.SET_ERROR, true);
                        commit(PREVENTIVE_WIDGET.SET_CIRCLE_LOADING, false);
                        await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined');
                    }
                    const preventiveData: PreventiveData = response.data;
                    commit(PREVENTIVE_WIDGET.SET_CIRCLE_ITEMS, {
                        inProgressTotal: preventiveData.nbEnCours,
                        achievedTotal: preventiveData.nbRealisees,
                        awaitingRealizationTotal: preventiveData.nbARealiser,
                    });
                    commit(PREVENTIVE_WIDGET.SET_CIRCLE_LOADING, false);
                } else {
                    commit(PREVENTIVE_WIDGET.SET_ERROR, true);
                    commit(PREVENTIVE_WIDGET.SET_CIRCLE_LOADING, false);
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined');
                }
            })
            .catch(async error => {
                commit(PREVENTIVE_WIDGET.SET_ERROR, true);
                commit(PREVENTIVE_WIDGET.SET_CIRCLE_LOADING, false);
                await ErrorManager.processError(error);
            });

    },

    async [PreventiveAction.GET_PREVENTIVE_CHART_DATA](
        { commit }: PreventiveActionType,
        payload: PreventiveDateParameters
    ): Promise<void> {
        commit(PREVENTIVE_WIDGET.SET_CHART_LOADING, true);
        await HomeService.getPreventiveChartData(payload)
            .then(async response => {
                if (response !== undefined || response.status !== undefined) {
                    if (response.status !== 200) {
                        commit(PREVENTIVE_WIDGET.SET_ERROR, true);
                        commit(PREVENTIVE_WIDGET.SET_CHART_LOADING, false);
                        commit(
                            `errorToaster/${ERROR.SET_MESSAGE}`,
                            await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                            { root: true }
                        );
                    }
                    const preventiveData: PreventiveData[] = response.data;
                    const enCours: number[] = [];
                    const realisees: number[] = [];
                    const aRealiser: number[] = [];
                    preventiveData.forEach((element: PreventiveData) => {
                        enCours.push(element.nbEnCours);
                        realisees.push(element.nbRealisees);
                        aRealiser.push(element.nbARealiser);
                    });
                    commit(PREVENTIVE_WIDGET.SET_CHART_ITEMS, {
                        inProgress: enCours,
                        achieved: realisees,
                        awaitingRealization: aRealiser,
                    });
                    commit(PREVENTIVE_WIDGET.SET_CHART_LOADING, false);
                } else {
                    commit(PREVENTIVE_WIDGET.SET_ERROR, true);
                    commit(PREVENTIVE_WIDGET.SET_CHART_LOADING, false);
                    commit(
                        `errorToaster/${ERROR.SET_MESSAGE}`,
                        await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                        { root: true }
                    );
                }
            })
            .catch(async error => {
                commit(PREVENTIVE_WIDGET.SET_ERROR, true);
                commit(PREVENTIVE_WIDGET.SET_CHART_LOADING, false);
                commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
            });
    },
};

const mutations: MutationTree<PreventiveState> = {
    [PREVENTIVE_WIDGET.SET_CIRCLE_LOADING](state: PreventiveState, isLoading: boolean): void {
        state.isCircleLoading = isLoading;
    },
    [PREVENTIVE_WIDGET.SET_CHART_LOADING](state: PreventiveState, isLoading: boolean): void {
        state.isChartLoading = isLoading;
    },
    [PREVENTIVE_WIDGET.SET_ERROR](state: PreventiveState, error: boolean): void {
        state.error = error;
    },
    [PREVENTIVE_WIDGET.SET_CIRCLE_ITEMS](state: PreventiveState, items: PreventiveCircleWidgetItems): void {
        state.preventiveCircleItems = items;
    },
    [PREVENTIVE_WIDGET.SET_CHART_ITEMS](state: PreventiveState, items: PreventiveChartWidgetItems): void {
        state.preventiveChartItems = items;
    },
    [PREVENTIVE_WIDGET.RESET](state: PreventiveState): void {
        Object.assign(state, initialState() );
    },
};

const widgetPreventif = {
    namespaced: true,
    state: initialState,
    actions,
    mutations,
};

export default widgetPreventif;
